/**
 * TransferDirectorshipModal
 *
 * Permite al director actual del club transferir su rol a otro miembro
 * que ya tenga cuenta de usuario vinculada.
 *
 * Llama a la Cloud Function callable `transferClubDirectorship` que valida
 * permisos en backend (solo director actual o admin de unión/asociación)
 * y ejecuta el cambio de forma atómica.
 *
 * Tras una transferencia exitosa el usuario actual deja de ser director,
 * por lo que se cierra sesión y se redirige al login.
 */

import { useMemo, useState } from "react";
import { httpsCallable } from "firebase/functions";
import { signOut } from "firebase/auth";
import { useNavigate } from "react-router-dom";
import {
  AlertTriangle,
  Loader2,
  Search,
  ShieldCheck,
  UserCheck,
} from "lucide-react";

import { Modal, ModalFooter, Button, Badge } from "@/components/ui";
import { auth, functions } from "@/lib/firebase";
import { useMembersByClub } from "@/hooks/usePersons";
import type { MemberWithPerson } from "@/types";

interface TransferDirectorshipModalProps {
  isOpen: boolean;
  onClose: () => void;
  clubId: string;
  clubName: string;
  /** uid del director actual (caller). Sirve para excluirlo del listado. */
  currentDirectorUserId: string | undefined;
}

interface TransferResponse {
  success: boolean;
  newRoleId: string;
  newDirectorUserId: string;
}

export default function TransferDirectorshipModal({
  isOpen,
  onClose,
  clubId,
  clubName,
  currentDirectorUserId,
}: TransferDirectorshipModalProps) {
  const navigate = useNavigate();
  const { data: members, isLoading: loading } = useMembersByClub(clubId);

  const [search, setSearch] = useState("");
  const [selectedPersonId, setSelectedPersonId] = useState<string | null>(null);
  const [confirmName, setConfirmName] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  // Solo miembros activos, con cuenta vinculada, y distintos del director actual.
  const eligible = useMemo(() => {
    if (!members) return [] as MemberWithPerson[];
    return members
      .filter((m) => m.status === "active")
      .filter((m) => !!m.person?.linkedUserId)
      .filter((m) => m.person.linkedUserId !== currentDirectorUserId)
      .sort((a, b) =>
        (a.person.fullName || "").localeCompare(b.person.fullName || ""),
      );
  }, [members, currentDirectorUserId]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return eligible;
    return eligible.filter((m) => {
      const name = (m.person.fullName || "").toLowerCase();
      const email = (m.person.email || "").toLowerCase();
      return name.includes(q) || email.includes(q);
    });
  }, [eligible, search]);

  const selected = useMemo(
    () => eligible.find((m) => m.person.id === selectedPersonId) || null,
    [eligible, selectedPersonId],
  );

  const expectedConfirm = (selected?.person.fullName || "").trim();
  const confirmMatches =
    !!selected &&
    confirmName.trim().toLowerCase() === expectedConfirm.toLowerCase();

  const handleClose = () => {
    if (submitting) return;
    setSearch("");
    setSelectedPersonId(null);
    setConfirmName("");
    setError(null);
    setDone(false);
    onClose();
  };

  const handleTransfer = async () => {
    if (!selected || !confirmMatches) return;
    setSubmitting(true);
    setError(null);
    try {
      const fn = httpsCallable<
        { clubId: string; newPersonId: string },
        TransferResponse
      >(functions, "transferClubDirectorship");
      await fn({ clubId, newPersonId: selected.person.id });
      setDone(true);
    } catch (e) {
      const msg =
        e instanceof Error ? e.message : "No se pudo transferir la dirección.";
      setError(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const handleAcceptAndLogout = async () => {
    try {
      await signOut(auth);
    } catch {
      // ignorar
    }
    navigate("/login", { replace: true });
    // Reload completo para limpiar todo el estado en memoria
    window.location.reload();
  };

  // ===== Pantalla de éxito =====
  if (done) {
    return (
      <Modal
        isOpen={isOpen}
        onClose={handleClose}
        title="Dirección transferida"
        size="md"
        closeOnOverlay={false}
        closeOnEscape={false}
      >
        <div className="space-y-4 text-center py-2">
          <div className="mx-auto w-14 h-14 rounded-full bg-emerald-100 flex items-center justify-center">
            <ShieldCheck className="w-7 h-7 text-emerald-600" />
          </div>
          <h3 className="text-lg font-semibold text-gray-900">
            ¡Listo! La dirección de {clubName} ahora es de{" "}
            {selected?.person.fullName ?? "el nuevo director"}.
          </h3>
          <p className="text-sm text-gray-600">
            A partir de ahora tú dejas de ser director y quedas como miembro
            regular del club. Necesitamos cerrar tu sesión para refrescar tus
            permisos.
          </p>
        </div>
        <ModalFooter>
          <Button onClick={handleAcceptAndLogout} className="w-full">
            Cerrar sesión y continuar
          </Button>
        </ModalFooter>
      </Modal>
    );
  }

  // ===== Pantalla principal =====
  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title="Transferir dirección del club"
      description="Elige al nuevo director. Debe ser un miembro activo del club que ya tenga cuenta de usuario vinculada."
      size="lg"
      closeOnOverlay={!submitting}
      closeOnEscape={!submitting}
    >
      <div className="space-y-4">
        {/* Aviso */}
        <div className="flex gap-3 rounded-xl border border-amber-200 bg-amber-50 p-3">
          <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
          <div className="text-sm text-amber-800 space-y-1">
            <p className="font-medium">
              Esta acción es inmediata y no puede deshacerse desde la app.
            </p>
            <p>
              Tras confirmar perderás los permisos de director y quedarás como
              miembro regular del club. El nuevo director recibirá acceso
              completo al panel.
            </p>
          </div>
        </div>

        {/* Buscador */}
        <div className="relative">
          <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Buscar miembro por nombre o correo…"
            className="w-full pl-9 pr-3 py-2 border-2 border-gray-200 rounded-xl focus:ring-4 focus:ring-purple-500/20 focus:border-purple-500 transition-all"
            disabled={submitting}
          />
        </div>

        {/* Lista */}
        <div className="border border-gray-200 rounded-xl max-h-72 overflow-y-auto divide-y divide-gray-100">
          {loading && (
            <div className="flex items-center justify-center py-8 text-gray-500 text-sm">
              <Loader2 className="w-4 h-4 animate-spin mr-2" />
              Cargando miembros…
            </div>
          )}
          {!loading && filtered.length === 0 && (
            <div className="px-4 py-8 text-center text-sm text-gray-500">
              {eligible.length === 0
                ? "No hay miembros con cuenta vinculada en este club. Pídele al miembro que se registre y vincule su cuenta antes de transferirle la dirección."
                : "Sin resultados."}
            </div>
          )}
          {!loading &&
            filtered.map((m) => {
              const isSelected = selectedPersonId === m.person.id;
              return (
                <button
                  key={m.person.id}
                  type="button"
                  onClick={() => {
                    setSelectedPersonId(m.person.id);
                    setConfirmName("");
                  }}
                  disabled={submitting}
                  className={`w-full text-left px-4 py-3 flex items-center gap-3 transition-colors ${
                    isSelected
                      ? "bg-purple-50 hover:bg-purple-50"
                      : "hover:bg-gray-50"
                  }`}
                >
                  <div
                    className={`w-9 h-9 rounded-full flex items-center justify-center text-sm font-semibold ${
                      isSelected
                        ? "bg-purple-600 text-white"
                        : "bg-gray-100 text-gray-600"
                    }`}
                  >
                    {(m.person.fullName || "?").charAt(0).toUpperCase()}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-gray-900 truncate">
                      {m.person.fullName || "Sin nombre"}
                    </div>
                    <div className="text-xs text-gray-500 truncate">
                      {m.person.email || "—"}
                    </div>
                  </div>
                  {m.role && m.role !== "member" && (
                    <Badge variant="default" size="sm">
                      {m.role}
                    </Badge>
                  )}
                  {isSelected && (
                    <UserCheck className="w-5 h-5 text-purple-600" />
                  )}
                </button>
              );
            })}
        </div>

        {/* Confirmación por escritura */}
        {selected && (
          <div className="space-y-2 rounded-xl border border-gray-200 p-4 bg-gray-50">
            <p className="text-sm text-gray-700">
              Para confirmar, escribe el nombre completo del nuevo director:{" "}
              <span className="font-semibold text-gray-900">
                {expectedConfirm}
              </span>
            </p>
            <input
              type="text"
              value={confirmName}
              onChange={(e) => setConfirmName(e.target.value)}
              placeholder={expectedConfirm}
              className="w-full px-3 py-2 border-2 border-gray-200 rounded-xl focus:ring-4 focus:ring-purple-500/20 focus:border-purple-500 transition-all"
              disabled={submitting}
            />
          </div>
        )}

        {/* Error */}
        {error && (
          <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-xl px-3 py-2">
            {error}
          </div>
        )}
      </div>

      <ModalFooter>
        <Button variant="outline" onClick={handleClose} disabled={submitting}>
          Cancelar
        </Button>
        <Button
          onClick={handleTransfer}
          disabled={!selected || !confirmMatches || submitting}
          className="bg-gradient-to-r from-rose-500 to-rose-600"
        >
          {submitting ? (
            <>
              <Loader2 className="w-4 h-4 animate-spin mr-2" />
              Transfiriendo…
            </>
          ) : (
            "Transferir dirección"
          )}
        </Button>
      </ModalFooter>
    </Modal>
  );
}
